/**
 * Token Management Module
 *
 * Handles JWT token retrieval, validation, and caching for Frame authentication.
 */

import { ApiEndpoints, StorageKeys } from "./config.js";
import { getTokenPayload, requestToken } from "./shared-utils.js";

/**
 * A decoded JWT token payload.
 * The `exp` field is a Unix timestamp (seconds) indicating when the token expires.
 * Additional claims may be present but are typed as unknown.
 */
export interface DecodedToken {
  readonly exp: number;
  readonly [key: string]: unknown;
}

/**
 * Checks if a JWT token payload is valid for more than 30 minutes.
 *
 * @param payload - Decoded JWT payload with 'exp' field.
 * @returns True if token expires in more than 30 minutes, false otherwise.
 */
export function isTokenValid(payload: DecodedToken): boolean {
  return payload.exp * 1000 - Date.now() > 30 * 60 * 1000;
}

/**
 * Retrieves a valid Frame authentication token from localStorage or requests a new one.
 *
 * Checks localStorage for an existing token. If found, validates that it has more than
 * 30 minutes remaining before expiration. If the token is missing or expires soon,
 * requests a new token from the server and caches it in localStorage.
 *
 * @returns A valid Frame authentication token.
 * @throws {Error} If token request fails or token validation fails.
 */
export async function getToken(): Promise<string> {
  let token = localStorage.getItem(StorageKeys.FRAME_TOKEN);
  if (token === null) {
    token = await requestToken(ApiEndpoints.TOKEN);
  }
  const payload = getTokenPayload(token) as DecodedToken;
  if (!isTokenValid(payload)) {
    token = await requestToken(ApiEndpoints.TOKEN);
  }
  localStorage.setItem(StorageKeys.FRAME_TOKEN, token);

  return token;
}
